import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  LogIn, LogOut, ArrowLeftRight, CalendarDays, Users, Building2, DoorOpen,
  Plus, CalendarPlus, UserPlus, Wrench, ChevronLeft, ChevronRight, ChevronDown, MapPin, TrendingUp
} from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Area, AreaChart } from 'recharts';

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState({ check_ins: 0, check_outs: 0, turnarounds: 0, reservations_on_date: 0, guests_on_date: 0, checkin_details: [], checkout_details: [], turnaround_details: [] });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [occupancyTrend, setOccupancyTrend] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    api.get('/dashboard').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
    api.get('/dashboard/occupancy-trend').then(r => setOccupancyTrend(r.data.trend || [])).catch(console.error);
  }, []);

  const fetchActivity = useCallback((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    api.get('/dashboard/activity', { params: { date: dateStr } })
      .then(r => setActivity(r.data))
      .catch(console.error);
  }, []);

  useEffect(() => { fetchActivity(selectedDate); }, [selectedDate, fetchActivity]);

  const prevDay = () => setSelectedDate(d => subDays(d, 1));
  const nextDay = () => setSelectedDate(d => addDays(d, 1));

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
      <div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" />
    </div>
  );
  if (!data) return <div data-testid="dashboard-error">Failed to load dashboard</div>;

  return (
    <div data-testid="dashboard-page" className="max-w-5xl mx-auto">
      {/* Welcome Header */}
      <div className="flex items-center justify-between mb-8 pb-5 border-b border-slate-200">
        <div>
          <p className="text-sm text-slate-500 mb-0.5">Welcome back,</p>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight" data-testid="dashboard-welcome">
            {user?.first_name} {user?.last_name}
          </h1>
        </div>
        <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-lg" data-testid="dashboard-avatar">
          {user?.first_name?.[0]}{user?.last_name?.[0]}
        </div>
      </div>

      {/* Activity Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold font-heading text-slate-900" data-testid="activity-title">Activity</h2>
          <div className="flex items-center gap-2">
            <button onClick={prevDay} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-blue-600 transition-colors" data-testid="activity-prev-day">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 hover:border-slate-400 transition-colors cursor-pointer" data-testid="activity-date">
                  <CalendarDays className="w-4 h-4 text-slate-400" />
                  {format(selectedDate, 'EEE, MMM d')}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="center" data-testid="calendar-popover">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => { if (date) { setSelectedDate(date); setCalendarOpen(false); } }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <button onClick={nextDay} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-blue-600 transition-colors" data-testid="activity-next-day">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <button
            onClick={() => setExpanded(expanded === 'checkins' ? null : 'checkins')}
            className={`bg-white border rounded-xl p-5 flex flex-col items-center text-center transition-all duration-200 ${expanded === 'checkins' ? 'border-emerald-400 shadow-md ring-1 ring-emerald-200' : 'border-slate-200 hover:shadow-md hover:border-slate-300'}`}
            data-testid="stat-check-ins"
          >
            <div className="w-11 h-11 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
              <LogIn className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="text-3xl font-bold font-heading text-emerald-600">{activity.check_ins}</p>
            <p className="text-xs text-slate-500 mt-1 font-medium">Check-ins</p>
            {activity.check_ins > 0 && <ChevronDown className={`w-4 h-4 text-slate-400 mt-2 transition-transform ${expanded === 'checkins' ? 'rotate-180' : ''}`} />}
          </button>
          <button
            onClick={() => setExpanded(expanded === 'checkouts' ? null : 'checkouts')}
            className={`bg-white border rounded-xl p-5 flex flex-col items-center text-center transition-all duration-200 ${expanded === 'checkouts' ? 'border-amber-400 shadow-md ring-1 ring-amber-200' : 'border-slate-200 hover:shadow-md hover:border-slate-300'}`}
            data-testid="stat-check-outs"
          >
            <div className="w-11 h-11 rounded-full bg-amber-50 flex items-center justify-center mb-3">
              <LogOut className="w-5 h-5 text-amber-600" />
            </div>
            <p className="text-3xl font-bold font-heading text-amber-600">{activity.check_outs}</p>
            <p className="text-xs text-slate-500 mt-1 font-medium">Check-outs</p>
            {activity.check_outs > 0 && <ChevronDown className={`w-4 h-4 text-slate-400 mt-2 transition-transform ${expanded === 'checkouts' ? 'rotate-180' : ''}`} />}
          </button>
          <button
            onClick={() => setExpanded(expanded === 'turnarounds' ? null : 'turnarounds')}
            className={`bg-white border rounded-xl p-5 flex flex-col items-center text-center transition-all duration-200 ${expanded === 'turnarounds' ? 'border-rose-400 shadow-md ring-1 ring-rose-200' : 'border-slate-200 hover:shadow-md hover:border-slate-300'}`}
            data-testid="stat-turnarounds"
          >
            <div className="w-11 h-11 rounded-full bg-rose-50 flex items-center justify-center mb-3">
              <ArrowLeftRight className="w-5 h-5 text-rose-500" />
            </div>
            <p className="text-3xl font-bold font-heading text-rose-500">{activity.turnarounds}</p>
            <p className="text-xs text-slate-500 mt-1 font-medium">Turnarounds</p>
            {activity.turnarounds > 0 && <ChevronDown className={`w-4 h-4 text-slate-400 mt-2 transition-transform ${expanded === 'turnarounds' ? 'rotate-180' : ''}`} />}
          </button>
        </div>

        {/* Expanded detail panels */}
        {expanded === 'checkins' && activity.checkin_details?.length > 0 && (
          <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="checkins-detail-panel">
            <h4 className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Check-in Details</h4>
            {activity.checkin_details.map(item => (
              <div key={item.id} className="bg-white rounded-lg p-3 flex items-center justify-between border border-emerald-100">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <LogIn className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{item.unit_name}</p>
                    <p className="text-xs text-slate-500">{item.property_name}</p>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <p className="text-sm font-medium text-slate-800">{item.guest_name}</p>
                  <div className="flex items-center gap-1.5 justify-end mt-0.5">
                    <Badge variant="outline" className="text-[10px] capitalize">{item.source?.replace('ical_', '')}</Badge>
                    <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">{item.status}</Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {expanded === 'checkouts' && activity.checkout_details?.length > 0 && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="checkouts-detail-panel">
            <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wider">Check-out Details</h4>
            {activity.checkout_details.map(item => (
              <div key={item.id} className="bg-white rounded-lg p-3 flex items-center justify-between border border-amber-100">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                    <LogOut className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{item.unit_name}</p>
                    <p className="text-xs text-slate-500">{item.property_name}</p>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <p className="text-sm font-medium text-slate-800">{item.guest_name}</p>
                  <div className="flex items-center gap-1.5 justify-end mt-0.5">
                    <Badge variant="outline" className="text-[10px] capitalize">{item.source?.replace('ical_', '')}</Badge>
                    <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">{item.status}</Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {expanded === 'turnarounds' && activity.turnaround_details?.length > 0 && (
          <div className="mt-4 bg-rose-50 border border-rose-200 rounded-xl p-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="turnarounds-detail-panel">
            <h4 className="text-xs font-bold text-rose-800 uppercase tracking-wider">Turnaround Details</h4>
            {activity.turnaround_details.map((item, i) => (
              <div key={i} className="bg-white rounded-lg p-3 border border-rose-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                    <ArrowLeftRight className="w-4 h-4 text-rose-500" />
                  </div>
                  <p className="text-sm font-semibold text-slate-900">{item.unit_name}</p>
                </div>
                <div className="grid grid-cols-2 gap-2 ml-12">
                  <div className="flex items-center gap-2 text-xs">
                    <LogOut className="w-3 h-3 text-amber-600" />
                    <span className="text-slate-500">Out:</span>
                    <span className="font-medium text-slate-800">{item.checkout_guest || 'Guest'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <LogIn className="w-3 h-3 text-emerald-600" />
                    <span className="text-slate-500">In:</span>
                    <span className="font-medium text-slate-800">{item.checkin_guest || 'Guest'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {expanded && (
          (expanded === 'checkins' && !activity.checkin_details?.length) ||
          (expanded === 'checkouts' && !activity.checkout_details?.length) ||
          (expanded === 'turnarounds' && !activity.turnaround_details?.length)
        ) && (
          <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-6 text-center text-sm text-slate-400">
            No details for this date
          </div>
        )}
      </div>

      {/* Stats Grid — Date specific */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col items-center text-center hover:shadow-md hover:border-slate-300 transition-all duration-200 cursor-pointer" onClick={() => navigate('/reservations')} data-testid="stat-reservations">
          <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-3">
            <CalendarDays className="w-6 h-6 text-amber-600" />
          </div>
          <p className="text-3xl font-bold font-heading text-slate-900">{activity.reservations_on_date || 0}</p>
          <p className="text-sm text-slate-500 mt-1">Reservations</p>
          <p className="text-[10px] text-slate-400 mt-0.5">on {format(selectedDate, 'MMM d')}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col items-center text-center hover:shadow-md hover:border-slate-300 transition-all duration-200 cursor-pointer" onClick={() => navigate('/guests')} data-testid="stat-guests">
          <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center mb-3">
            <Users className="w-6 h-6 text-purple-600" />
          </div>
          <p className="text-3xl font-bold font-heading text-slate-900">{activity.guests_on_date || 0}</p>
          <p className="text-sm text-slate-500 mt-1">Guests</p>
          <p className="text-[10px] text-slate-400 mt-0.5">on {format(selectedDate, 'MMM d')}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col items-center text-center hover:shadow-md hover:border-slate-300 transition-all duration-200 cursor-pointer" onClick={() => navigate('/properties')} data-testid="stat-properties">
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-3">
            <Building2 className="w-6 h-6 text-blue-600" />
          </div>
          <p className="text-3xl font-bold font-heading text-slate-900">{data.total_properties}</p>
          <p className="text-sm text-slate-500 mt-1">Properties</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col items-center text-center hover:shadow-md hover:border-slate-300 transition-all duration-200 cursor-pointer" onClick={() => navigate('/units')} data-testid="stat-units">
          <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
            <DoorOpen className="w-6 h-6 text-emerald-600" />
          </div>
          <p className="text-3xl font-bold font-heading text-slate-900">{data.total_units}</p>
          <p className="text-sm text-slate-500 mt-1">Units</p>
        </div>
      </div>

      {/* Occupancy Trend & Sources Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="occupancy-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 uppercase tracking-wider">Occupancy Trend (30 Days)</h3>
          {occupancyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={occupancyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} interval={4} />
                <YAxis tick={{ fontSize: 12, fill: '#64748b' }} domain={[0, 100]} unit="%" />
                <Tooltip formatter={(v) => `${v}%`} />
                <Area type="monotone" dataKey="occupancy" stroke="#2563EB" fill="#2563EB" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 py-8 text-center">No occupancy data yet</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="source-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 uppercase tracking-wider">Booking Sources</h3>
          {data.source_breakdown?.length > 0 ? (
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              <ResponsiveContainer width="100%" height={180} className="sm:w-1/2">
                <PieChart>
                  <Pie data={data.source_breakdown} dataKey="count" nameKey="_id" cx="50%" cy="50%" outerRadius={75} innerRadius={35}>
                    {data.source_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {data.source_breakdown.map((s, i) => (
                  <div key={s._id} className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <span className="text-slate-600">{s._id || 'unknown'}</span>
                    <span className="font-semibold text-slate-900">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-sm text-slate-400 py-8 text-center">No source data yet</p>}
        </div>
      </div>

      {/* Upcoming Check-ins/outs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="upcoming-checkins">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 uppercase tracking-wider">Upcoming Check-ins</h3>
          {data.upcoming_checkins?.length > 0 ? (
            <div className="space-y-3">
              {data.upcoming_checkins.map(r => (
                <div key={r.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{r.guest_name || r.notes || 'Guest'}</p>
                    <p className="text-xs text-slate-500">Unit: {r.unit_id?.slice(-6)}</p>
                  </div>
                  <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">{r.check_in ? format(new Date(r.check_in), 'MMM dd') : 'N/A'}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 text-center py-4">No upcoming check-ins</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="upcoming-checkouts">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 uppercase tracking-wider">Upcoming Check-outs</h3>
          {data.upcoming_checkouts?.length > 0 ? (
            <div className="space-y-3">
              {data.upcoming_checkouts.map(r => (
                <div key={r.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{r.guest_name || r.notes || 'Guest'}</p>
                    <p className="text-xs text-slate-500">Unit: {r.unit_id?.slice(-6)}</p>
                  </div>
                  <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded">{r.check_out ? format(new Date(r.check_out), 'MMM dd') : 'N/A'}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 text-center py-4">No upcoming check-outs</p>}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-lg font-bold font-heading text-slate-900 mb-4" data-testid="quick-actions-title">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-4">
          <button onClick={() => navigate('/properties')} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col items-center text-center hover:shadow-md hover:border-slate-300 transition-all duration-200 group" data-testid="quick-add-property">
            <div className="w-11 h-11 rounded-full bg-blue-50 flex items-center justify-center mb-3 group-hover:bg-blue-100 transition-colors">
              <Plus className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-sm font-semibold text-slate-900">Add Property</p>
          </button>
          <button onClick={() => navigate('/reservations')} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col items-center text-center hover:shadow-md hover:border-slate-300 transition-all duration-200 group" data-testid="quick-new-booking">
            <div className="w-11 h-11 rounded-full bg-emerald-50 flex items-center justify-center mb-3 group-hover:bg-emerald-100 transition-colors">
              <CalendarPlus className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="text-sm font-semibold text-slate-900">New Booking</p>
          </button>
          <button onClick={() => navigate('/guests')} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col items-center text-center hover:shadow-md hover:border-slate-300 transition-all duration-200 group" data-testid="quick-add-guest">
            <div className="w-11 h-11 rounded-full bg-amber-50 flex items-center justify-center mb-3 group-hover:bg-amber-100 transition-colors">
              <UserPlus className="w-5 h-5 text-amber-600" />
            </div>
            <p className="text-sm font-semibold text-slate-900">Add Guest</p>
          </button>
          <button onClick={() => navigate('/maintenance')} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col items-center text-center hover:shadow-md hover:border-slate-300 transition-all duration-200 group" data-testid="quick-maintenance">
            <div className="w-11 h-11 rounded-full bg-rose-50 flex items-center justify-center mb-3 group-hover:bg-rose-100 transition-colors">
              <Wrench className="w-5 h-5 text-rose-500" />
            </div>
            <p className="text-sm font-semibold text-slate-900">Maintenance</p>
          </button>
        </div>
      </div>
    </div>
  );
}
